import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ClipboardList, Plus, Edit2, Trash2, Search, X, Save, Tag } from 'lucide-react';

const CATEGORIES = ['sheet', 'tile', 'ridge', 'valley', 'barge', 'spouting', 'fastening', 'flashing', 'cleaning', 'recoat', 'aquaseal', 'safety', 'travel', 'urgent', 'misc', 'underlay'];
const CATEGORY_LABELS = {
  sheet: 'Sheet', tile: 'Tiles', ridge: 'Ridge', valley: 'Valley', barge: 'Barge',
  spouting: 'Spouting', fastening: 'Fastening', flashing: 'Flashing', cleaning: 'Cleaning',
  recoat: 'Recoat', aquaseal: 'Aquaseal', safety: 'Safety', travel: 'Travel',
  urgent: 'Urgent', misc: 'Misc', underlay: 'Underlay',
};
const ROOF_TYPES = ['Concrete Tile', 'Clay Tile', 'Modern Concrete', 'Old Concrete', 'Long Run Iron', 'Short Run Iron', 'Iron', 'Tin Tile', 'Decramastic', 'Butynol', 'Polycarbonate', 'Asbestos', 'Metal Tile', 'Shake/Shingle', 'Slate', 'Corrugate', 'Tray', 'Glass'];

export default function ItemRequirementsPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('sheet');
  const [roofTypeFilter, setRoofTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ category: 'sheet', requirement: '', roof_types: [] });

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/item-requirements?category=${activeCategory}`;
      if (roofTypeFilter) url += `&roof_type=${roofTypeFilter}`;
      if (search) url += `&search=${search}`;
      const { data } = await api.get(url);
      setItems(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, activeCategory, roofTypeFilter, search]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const saveItem = async () => {
    try {
      if (editItem?._id) {
        await api.put(`/item-requirements/${editItem._id}`, form);
      } else {
        await api.post('/item-requirements', form);
      }
      setShowForm(false);
      setEditItem(null);
      setForm({ category: activeCategory, requirement: '', roof_types: [] });
      loadItems();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save'); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this requirement?')) return;
    await api.delete(`/item-requirements/${id}`);
    loadItems();
  };

  const startEdit = (item) => {
    setEditItem(item);
    setForm({ category: item.category, requirement: item.requirement, roof_types: item.roof_types || [] });
    setShowForm(true);
  };

  const startNew = () => {
    setEditItem(null);
    setForm({ category: activeCategory, requirement: '', roof_types: [] });
    setShowForm(true);
  };

  const toggleRoofType = (rt) => {
    setForm(f => ({
      ...f,
      roof_types: f.roof_types.includes(rt) ? f.roof_types.filter(r => r !== rt) : [...f.roof_types, rt]
    }));
  };

  return (
    <div data-testid="item-requirements-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Item Requirements</h1>
          <p className="text-sm text-slate-500 mt-1">Manage requirement descriptions per item category and roof type. These appear as selectable options on appraisals.</p>
        </div>
        <button data-testid="add-requirement-btn" onClick={startNew} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000]">
          <Plus size={16} /> Add Requirement
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => { setActiveCategory(c); setSearch(''); }} className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${activeCategory === c ? 'bg-white text-[#D35400] shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
              {CATEGORY_LABELS[c] || c}
            </button>
          ))}
        </div>
        <select data-testid="roof-type-filter" value={roofTypeFilter} onChange={e => setRoofTypeFilter(e.target.value)} className="text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white">
          <option value="">All Roof Types</option>
          {ROOF_TYPES.map(rt => <option key={rt} value={rt}>{rt}</option>)}
        </select>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="req-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white border-2 border-[#D35400] rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-bold text-[#2C2C2C]">{editItem ? 'Edit Requirement' : 'New Requirement'}</h3>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Requirement Text *</label>
            <textarea data-testid="req-text-input" value={form.requirement} onChange={e => setForm({...form, requirement: e.target.value})} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" rows={2} placeholder="e.g. Remove old iron sheets and dispose of responsibly" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Category</label>
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="text-sm border border-[#E2E2DF] rounded-lg px-3 py-2">
              {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Applies to Roof Types <span className="text-slate-400 font-normal">(leave empty for all)</span></label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {ROOF_TYPES.map(rt => (
                <button key={rt} onClick={() => toggleRoofType(rt)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.roof_types.includes(rt) ? 'bg-[#D35400] text-white border-[#D35400]' : 'border-[#E2E2DF] text-slate-500 hover:border-[#D35400]'}`}>
                  {rt}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button data-testid="save-requirement-btn" onClick={saveItem} disabled={!form.requirement.trim()} className="px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] disabled:opacity-50">Save</button>
            <button onClick={() => { setShowForm(false); setEditItem(null); }} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm hover:bg-slate-50">Cancel</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-[#E2E2DF] flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{CATEGORY_LABELS[activeCategory] || activeCategory} Requirements — {items.length}</span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.map(item => (
            <div key={item._id} className="flex items-start gap-3 px-4 py-3 border-b border-[#E2E2DF] last:border-0 hover:bg-slate-50 group">
              <ClipboardList size={14} className="text-slate-300 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700">{item.requirement}</p>
                {(item.roof_types || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {item.roof_types.map(rt => (
                      <span key={rt} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">{rt}</span>
                    ))}
                  </div>
                )}
                {(!item.roof_types || item.roof_types.length === 0) && (
                  <span className="text-[10px] text-slate-400">All roof types</span>
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                <button data-testid={`edit-req-${item._id}`} onClick={() => startEdit(item)} className="p-1 text-slate-400 hover:text-[#D35400]"><Edit2 size={13} /></button>
                <button data-testid={`delete-req-${item._id}`} onClick={() => deleteItem(item._id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && !loading && <p className="px-4 py-8 text-center text-sm text-slate-400">No requirements for {CATEGORY_LABELS[activeCategory]}. Add one to get started.</p>}
        </div>
      </div>
    </div>
  );
}
